// deps
import React, { ReactElement, memo, useContext } from "react";
import invariant from "tiny-invariant";

// internal deps
import { PaymentGroupDataDef, MorePaymentOptions, SplitPayBannerList } from "@swiggy-private/payment-ui";
import { useEffectOnce } from "@swiggy-private/core-ui";

// components
import CommonHeaderContent from "@payment-client/components/common-header-content/CommonHeaderContent";
import CommonUIWrapper from "@payment-client/components/common-ui-wrapper/CommonUIWrapper";
import { PaymentActionProvider } from "@payment-client/components/show-payments/components/payment-action-provider";

// interfaces
import { RouteProps } from "@payment-client/interfaces/Router";

// contexts
import { PaymentPayContext } from "@payment-client/contexts/PaymentPayContext";
import { ShowPaymentContext } from "@payment-client/contexts/ShowPaymentContext";
import { AnalyticsContext } from "@swiggy-private/checkout-core";
import { SplitPayListContext } from "@payment-client/contexts/SplitPayListContext";

// hooks
import { useSwiggyMoneyActions } from "@payment-client/hooks/UseSwiggyMoneyActions";
import useSplitPay from "@payment-client/hooks/UseSplitPay";
import useFlipkartCoins from "@payment-client/hooks/useFlipkartCoins";

// constants
import { MISSING_ROUTE_DATA } from "@payment-client/constants/Route";
import { MORE_PAYMENTS_TEXTS } from "@payment-client/constants/MorePayments";

// analytics
import { morePaymentGroupsImpressionEvent } from "@payment-client/analytics/events/Payment";

// helpers
import { getFrictionLayoutId } from "@payment-client/helpers/PaymentHelper";
import { isEmpty } from "@swiggy-private/js-utils";

type MorePaymentGroupsRouteProps = RouteProps<{
    frictionGroups: PaymentGroupDataDef[];
}>;

export const MorePaymentGroups = memo(function MorePaymentGroups({
    data,
}: MorePaymentGroupsRouteProps): ReactElement | null {
    // payments data
    const { showSplitPay, selectedSplitPayMethods } = useContext(PaymentPayContext);
    const { paymentResponse } = useContext(ShowPaymentContext);
    const { splitPayList } = useContext(SplitPayListContext);

    // analytics
    const { impressionEvent } = useContext(AnalyticsContext);

    // swiggy money actions
    const { addMoneyActions, swiggyMoneyInfoActions } = useSwiggyMoneyActions();

    // split pay data
    const { onSplitPayToggle } = useSplitPay();

    const onFlipkartToolTipShow = useFlipkartCoins(true);

    invariant(data?.frictionGroups, MISSING_ROUTE_DATA);

    const header = <CommonHeaderContent title={MORE_PAYMENTS_TEXTS.MORE_PAYMENT_OPTIONS} grayscale />;
    const splitpayList = splitPayList ?? [];
    const isSplitPayBannerVisible = showSplitPay && !isEmpty(splitpayList);

    const content = (
        <PaymentActionProvider>
            {({ onMorePaymentsOptionSelect }): ReactElement => (
                <>
                    {isSplitPayBannerVisible && (
                        <SplitPayBannerList
                            selectedSplitpayMethods={selectedSplitPayMethods}
                            splitPayList={splitpayList}
                            handleChange={onSplitPayToggle}
                            addMoneyActions={addMoneyActions}
                            isFrictionLayout={true}
                            swiggyMoneyInfoActions={swiggyMoneyInfoActions}
                            onFlipkartToolTipShow={onFlipkartToolTipShow}
                            couponApplied={paymentResponse?.couponApplied}
                            rewardApplied={paymentResponse?.rewardApplied}
                        />
                    )}
                    <MorePaymentOptions
                        paymentGroups={data.frictionGroups}
                        onOptionClick={onMorePaymentsOptionSelect}
                    />
                </>
            )}
        </PaymentActionProvider>
    );

    // to send impression event
    useEffectOnce(() => {
        const frictionLayoutId = getFrictionLayoutId(paymentResponse);
        morePaymentGroupsImpressionEvent(frictionLayoutId, impressionEvent);
    });

    return <CommonUIWrapper header={header} content={content} />;
});
